import { action, computed, makeObservable, observable, reaction, runInAction, toJS } from 'mobx';
import { appendPage } from 'utils/appendPage';
import isUndefined from 'lodash/isUndefined';
import isNull from 'lodash/isNull';
import intl from 'react-intl-universal';
import { injector } from 'Injector';
import { Assignment, Article, ARTICLE_SERVICE_KEY, Attachment, Grade, QuestionAttachment, QuestionType, Subject, FilterArticlePanel, Source, CustomImgAttachment, Keyword } from 'assignment/Assignment';
import { DraftAssignment, EditableImageChoiceQuestion, EditableQuestion } from 'assignment/assignmentDraft/AssignmentDraft';
import { ArticleSearchParams, ArticleService, ASSIGNMENT_SERVICE, AssignmentService } from 'assignment/service';
import { bodyMessageOf, messageOf, statusOf } from 'utils/apiError';
import { DRAFT_ASSIGNMENT_SERVICE, DraftAssignmentService } from 'assignment/assignmentDraft/service';
import { DEFAULT_AMOUNT_ARTICLES_PER_PAGE, STATUS_FORBIDDEN, DEBOUNCE_TIME } from 'utils/constants';
import { EditEntityLocaleKeys, Locales } from 'utils/enums';
import { debounce } from 'utils/debounce';
import { DISTRIBUTION_SERVICE, DistributionService } from 'distribution/service';
import { USER_SERVICE, UserService } from 'user/UserService';
import { AssignmentContainer } from 'assignment/assignmentContainer/AssignmentContainer';
import { Distribution } from 'distribution/Distribution';
import { AttachmentContentType } from './AttachmentContentTypeContext';
import { Notification, NotificationTypes } from 'components/common/Notification/Notification';
import { TEACHING_PATH_SERVICE, TeachingPathService } from 'teachingPath/service';
import { reportFailure } from 'utils/reportFailure';

const statusCode204 = 204;
const numberOfImagesForSkeleton = 12;
const numberOfCustomImagesForSkeleton = 12;
const numberOfVideosForSkeleton = 4;
const delayFocus = 250;

const getAllChildArticlesIds = (article: Article) => {
  const qwe = article.levels![0] && article.levels![0].childArticles ? article.levels![0].childArticles.map(child => child.wpId || child.id) : [];
  return Array.from(new Set([Number(article.id), ...qwe]));
};

export enum CreationElements {
  Title = 'Title',
  Articles = 'Articles',
  Questions = 'Questions'
}

interface CreationElementsType {
  type: CreationElements;
  orderQuestion?: number;
}

interface CurrentContentBlock {
  orderQuestion: number;
  orderBlock: number;
}

interface CurrentContentBlock {
  orderQuestion: number;
  orderBlock: number;
}

export class NewAssignmentStore {
  private assignmentService: AssignmentService = injector.get<AssignmentService>(ASSIGNMENT_SERVICE);
  private draftAssignmentService: DraftAssignmentService = injector.get<DraftAssignmentService>(DRAFT_ASSIGNMENT_SERVICE);
  private distributionService: DistributionService = injector.get<DistributionService>(DISTRIBUTION_SERVICE);

  private articleService: ArticleService = injector.get<ArticleService>(ARTICLE_SERVICE_KEY);
  private userService: UserService = injector.get(USER_SERVICE);

  public currentArticlesPage: number = 1;

  public teachingPathService = injector.get<TeachingPathService>(TEACHING_PATH_SERVICE);

  @observable public fetchingArticles: boolean = false;
  @observable public visibilityArticles: boolean = false;
  @observable public hiddenArticles: boolean = false;
  @observable public isActiveButtons: boolean = false;
  @observable public isDisabledButtons: boolean = false;
  @observable public fetchingAttachments: boolean = false;
  @observable public fetchingCustomImageAttachments: boolean = false;
  @observable public visibilityAttachments: boolean = false;
  @observable public showValidationErrors: boolean = false;
  // @observable public open: boolean = false;
  @observable public assignmentContainer: AssignmentContainer | null = null;
  @observable public currentPreviewQuestion: number = -1;
  @observable public currentContentBlock: CurrentContentBlock = { orderQuestion: -1, orderBlock: -1 };
  @observable public allArticles: Array<Article> = [];
  @observable public isFetchedArticlesListFinished: boolean = false;
  public articlesForSkeleton: Array<Article> = new Array<Article>(DEFAULT_AMOUNT_ARTICLES_PER_PAGE).fill(new Article({ id: 0, title: '' }));
  @observable public allGrades: Array<Grade> = [];
  @observable public allSubjects: Array<Subject> = [];
  @observable public allSources: Array<Source> = [];
  @observable public allKeywords: Array<Keyword> = [];
  @observable public questionAttachments: Array<Attachment> = [];
  @observable public questionCustomAttachments: Array<CustomImgAttachment> = [];
  @observable public currentOrderOption: number = -1;
  @observable public searchValue: string = '';
  @observable public storedAssignment: Assignment | null = null;
  @observable public highlightingItem: CreationElementsType | undefined;
  @observable public allArticlePanelFilters: FilterArticlePanel | null = null;
  @observable public numberOfPages: number = 0;
  @observable public currentPage: number = 1;

  public arrayForImagesSkeleton = new Array<string>(numberOfImagesForSkeleton).fill('imageSkeletonLoader');
  public arrayForCustomImagesSkeleton = new Array<string>(numberOfCustomImagesForSkeleton).fill('imageSkeletonLoader');
  public arrayForVideosSkeleton = new Array<string>(numberOfVideosForSkeleton).fill('videoSkeletonLoader');

  public localeKey: EditEntityLocaleKeys = EditEntityLocaleKeys.NEW_ASSIGNMENT;

  // Through an arrow, so `this` is the store however the wrapper is called, and the method is
  // read when it runs, after makeObservable has wrapped it as an action.
  public getArticlesWithDebounce = debounce(
    (params: Parameters<NewAssignmentStore['getArticles']>[0]) => { void this.getArticles(params); },
    DEBOUNCE_TIME,
  );

  constructor() {
    makeObservable(this);
  }

  @action
  private buildAssignmentContainer(assignment: DraftAssignment, distribution?: Distribution) {
    this.assignmentContainer = new AssignmentContainer({
      assignment,
      distribution,
    });
  }

  @action
  public async getLocalesByid() {
    return this.assignmentService.getLocalesByApi();
  }

  @computed
  public get ifSearchValueIsNumber() {
    return !isNaN(Number(this.searchValue.charAt(0)));
  }

  /**
   * "Add" or "Edit" for the teacher-guidance button, derived rather than stored.
   *
   * This was an observable written by `setTitleButtonGuidance`, and the only thing that ever
   * called it was `TeacherGuidanceAssigModal.renderAssigGuidance` — **from inside render**.
   * Twelve components read this value, `AssignmentTitle` among them, and it renders before the
   * modal does. So every render of the editor wrote observable state that an
   * already-rendered component was observing, and React said so:
   *
   *     Cannot update during an existing state transition (such as within `render`).
   *     Render methods should be a pure function of props and state.
   *     <inject-with-newAssignmentStore(TeacherGuidanceAssigModal)>
   *
   * A getter is what it always was: a function of the assignment's guidance and its questions'
   * guidance. Nothing writes it, so nothing can write it at the wrong moment, and the twelve
   * readers are unchanged.
   *
   * Deliberately NOT `@computed`. A computed caches until one of its *observable* dependencies
   * changes, and `intl.get` is not observable — so after a language switch the button kept the
   * label from the previous locale. #149 shipped it as a computed and that was wrong; a plain
   * getter re-evaluates on every read, which is what the old render-time call did. Reactivity
   * to the assignment is unaffected: the observable reads happen inside the observer's render,
   * so mobx tracks them either way.
   */
  public get getTitleButtonGuidance() {
    const guidanceBlank1 = '<p><br></p>';
    const guidanceBlank2 = '';
    const isBlank = (guidance: string | undefined) => (
      guidance === guidanceBlank1 || guidance === guidanceBlank2 || guidance === undefined
    );

    const assignment = this.currentEntity;
    const hasGuidance = !!assignment && (
      !isBlank(assignment.guidance)
      || assignment.questions.some(question => !isBlank(question.guidance))
    );

    return hasGuidance
      ? intl.get('teacherGuidance.buttons.edit')
      : intl.get('teacherGuidance.buttons.add');
  }

  /**
   * `@action` because it writes `isHidden` on every related article — observable state that
   * `SetRelatedArticles`, `RelatedArticlesPreview` and the editor's preview all observe. It
   * had none, and the detector for class D never saw it: it is a method call at every call
   * site, not an assignment. See docs/mobx-6-class-d.md.
   */
  @action
  public relatedArticlesIsHidden() {
    this.currentEntity!.relatedArticles.forEach((e) => {
      e.isHidden = this.hiddenArticles;
    });
  }

  /**
   * The "hide the related articles" checkbox, as one change rather than two.
   *
   * Every caller set `hiddenArticles` and then called `relatedArticlesIsHidden()` on the next
   * line, from a component, outside any action — so observers saw the flag change before the
   * articles it describes had been updated. They are one decision and are applied together.
   */
  @action
  public setRelatedArticlesHidden(value: boolean) {
    this.hiddenArticles = value;
    this.relatedArticlesIsHidden();
  }

  /** Whether the related-articles panel is on screen. `Header` reads it to decide what its buttons do. */
  @action
  public setArticlesPanelVisible(value: boolean) {
    this.visibilityArticles = value;
  }

  @action
  public setIsActiveButtons() {
    this.isActiveButtons = true;
  }

  @action
  public setIsDisabledButtons() {
    this.isDisabledButtons = true;
  }

  @action
  public setIsActiveButtonsFalse() {
    this.isActiveButtons = false;
  }

  @action
  public setIsDisabledButtonsFalse() {
    this.isDisabledButtons = false;
  }

  @computed
  public get highlightedItem() {
    return this.highlightingItem;
  }

  @action
  public setCurrentPreviewQuestion(number: number) {
    this.currentPreviewQuestion = number;
  }

  public isHighlightedItem(type: CreationElements, orderQuestion?: number) {
    if (!isUndefined(orderQuestion) && this.highlightingItem && this.highlightingItem.type === type) {
      return this.highlightingItem.orderQuestion === orderQuestion;
    }
    return this.highlightingItem && this.highlightingItem.type === type;
  }

  @action
  public setHighlightingItem(type: CreationElements, orderQuestion?: number) {
    if (type === CreationElements.Questions && !isUndefined(orderQuestion)) {
      return this.highlightingItem = { orderQuestion, type: CreationElements.Questions };
    }
    return this.highlightingItem = { type, orderQuestion: undefined };
  }

  @action
  public isMultipleChoice() {
    const currentQuestion = this.currentContentBlock.orderQuestion;
    if (this.assignmentContainer) {
      const question = this.assignmentContainer.assignment.questions.find(question => question.orderPosition === currentQuestion) as EditableQuestion;
      if (question && !isUndefined(question.type) && question.type === QuestionType.ImageChoice) {
        return true;
      }
      return false;
    }
    return false;
  }

  @computed
  public get currentEntity() {
    return this.assignmentContainer && this.assignmentContainer.assignment;
  }

  @action
  public storeAssignment = () => {
    this.storedAssignment = new Assignment(this.assignmentContainer!.assignment);
  }

  @action
  public clearStoredAssignment = () => {
    this.storedAssignment = null;
  }

  @computed
  public get filteredGroups() {

    const filteredGroups = this.currentDistribution!.groups.filter(
      group => group.name.toLowerCase().search(this.searchValue) !== -1
    );
    return this.ifSearchValueIsNumber ? filteredGroups : this.currentDistribution!.groups;
  }

  @computed
  public get filteredStudents() {
    const filteredStudentsInGroups = this.currentDistribution!.groups.map(
      group => this.ifSearchValueIsNumber ?
        group.assignedStudents :
        group.assignedStudents.filter(student => student.name.toLowerCase().search(this.searchValue) !== -1)
    );

    return filteredStudentsInGroups;
  }

  @computed
  public get filteredStudentsWithoutFilters() {
    return this.currentDistribution!.groups.map(group => group.assignedStudents);
  }

  @computed
  public get currentDistribution() {
    return this.assignmentContainer && this.assignmentContainer.distribution;
  }

  @computed
  public get selectedGroups() {
    const selectedGroups = this.currentDistribution!.groups.filter(
      group => group.isSelected
    );

    return selectedGroups;
  }

  @action
  public getFilteredGroups = () => {
    const filteredGroups = this.currentDistribution!.groups
      .filter(
        group => group.name.toLowerCase().search(this.searchValue) !== -1
      );

    return filteredGroups;
  }

  @computed
  public get articlesAmount() {
    return toJS(this.allArticles).length;
  }

  @computed
  public get questionAttachmentsList() {
    return this.questionAttachments.slice();
  }

  public get currentQuestion(): EditableQuestion | undefined {
    if (this.currentPreviewQuestion >= 0) {
      return this.currentEntity!.questions[this.currentPreviewQuestion];
    }

    return undefined;
  }

  @computed
  public get getCurrentOption() {
    return this.currentOrderOption;
  }

  @action
  public setCurrentOption(orderOption: number) {
    this.currentOrderOption = orderOption;
  }

  @action
  public setImageCurrentOption(image: QuestionAttachment) {
    const currentImageChoiceQuestion = this.currentQuestion as EditableImageChoiceQuestion;
    const currentOption = currentImageChoiceQuestion.options.find(option => option.order === this.currentOrderOption);
    currentOption!.setImage(image);
  }

  @action
  public setCustomImageCurrentOption(image: QuestionAttachment) {
    const currentCustomImageChoiceQuestion = this.currentQuestion as EditableImageChoiceQuestion;
    const currentOption = currentCustomImageChoiceQuestion.options.find(option => option.order === this.currentOrderOption);
    currentOption!.setImage(image);
  }

  @action
  public clearCurrentOption() {
    this.currentOrderOption = -1;
  }

  @computed
  public get currentBlock() {
    return this.currentContentBlock;
  }

  @action
  public getAttachmentsFromCurrentBlock() {
    if (this.currentQuestion && this.currentQuestion.orderPosition === this.currentContentBlock.orderQuestion) {
      return this.currentQuestion.content.find(
        block => block.order === this.currentContentBlock.orderBlock);
    }
    return undefined;
  }

  @action
  public setCurrentContentBlock(orderQuestion: number, orderBlock: number) {
    this.currentContentBlock = { orderQuestion, orderBlock };
  }

  @action
  public addNewQuestion(type: QuestionType) {
    this.currentEntity!.setQuestionsWithError(null);
    this.currentEntity!.addNewQuestion(type);
    this.currentPreviewQuestion = this.currentEntity!.questions.length - 1;
  }

  @action
  public async saveAttachment(attachmentId: number) {
    // No try/catch: the one here never ran, since the call was returned unawaited, and
    // AttachmentsList depends on that. It reads the axios status off the error to say
    // "Attachment not found", which a rewrapped Error would not carry.
    return this.draftAssignmentService.saveAttachment(this.currentEntity!.id, attachmentId);
  }

  @action
  public async removeAttachment(attachmentId: number) {
    try {
      const statusCode = await this.draftAssignmentService.removeAttachment(this.currentEntity!.id, attachmentId);
      if (statusCode === statusCode204) {
        runInAction(() => {
          this.questionAttachments = this.questionAttachments.filter(
            attachment => attachment.id !== attachmentId
          );
        });
      }
    } catch (e) {
      throw new Error(`remove wp image ${messageOf(e)}`, { cause: e });
    }
  }

  public async createAssigment() {
    const assignment = await this.draftAssignmentService.getNewAssignment();
    this.buildAssignmentContainer(assignment);

    return this.currentEntity!;
  }

  /**
   * The id of a new draft to open the editor on, or undefined, having told the teacher, when
   * one could not be created. Always resolves: the ten buttons and shortcuts offering "new"
   * call it from click handlers.
   */
  public async newDraftId(): Promise<number | undefined> {
    try {
      return (await this.createAssigment()).id;
    } catch (error) {
      reportFailure(error);
      return undefined;
    }
  }

  public async getAssigmentForEditing(id: number, add?: boolean, localeid?: number) {
    try {
      if (add) {
        await this.draftAssignmentService.createAssignmentLocale(id, localeid);
      }
      const assignment = await this.draftAssignmentService.getDraftAssignmentForEditing(id, localeid);
      runInAction(() => {
        this.buildAssignmentContainer(assignment);
        this.currentPreviewQuestion = this.currentEntity!.questions.length - 1;
        this.setHighlightingItem(CreationElements.Title);
      });

      return toJS(this.currentEntity);
    } catch (error) {
      if (statusOf(error) === STATUS_FORBIDDEN) {
        return null;
      }

      // `error.data.message` before: an axios error has no `data`, the body is under
      // `response.data`, so this title has always been undefined.
      return Notification.create({
        type: NotificationTypes.ERROR,
        title: bodyMessageOf(error) ?? messageOf(error)
      });
    }
  }

  public async getDistributionData(id: number) {
    const distribution = await this.distributionService.getAssignmentDistributionData(id);

    this.buildAssignmentContainer(this.currentEntity!, distribution);

    return toJS(this.assignmentContainer);
  }

  /**
   * An action because `currentPreviewQuestion` is observed — the preview pane reads it — and
   * this runs on every question click, reached from `QuestionsList.setHighlightedItem`. A
   * browser stack trace under the `[MobX]` strict-mode warning named this exact line.
   *
   * Note the neighbour two methods up: `setCurrentPreviewQuestion` writes the same field and
   * has always been an action. This one had drifted.
   */
  @action
  public setPreviewQuestionByIndex(index: number): void {
    this.currentPreviewQuestion = index;
  }

  @action
  public async save(): Promise<boolean> {
    this.showValidationErrors = true;
    reaction(() => {
      if (this.currentEntity && this.currentEntity.isValid) {
        const result = this.currentEntity.isValid('save');
        if (!isNull(this.currentEntity.questionsWithErrors)) {
          this.setHighlightingItem(CreationElements.Questions, this.currentEntity.questionsWithErrors);
          this.setPreviewQuestionByIndex(this.currentEntity.questionsWithErrors);
        }
        return result;
      }

    }, (isValid, previousValue, reactionDisposer) => {
      if (isValid) {
        this.showValidationErrors = false;
        this.currentEntity!.setQuestionsWithError(null);
        reactionDisposer.dispose();
      }
    });
    return this.currentEntity!.saveAssignment();
  }

  @action
  public async publish(): Promise<void> {
    this.showValidationErrors = true;
    reaction(() => this.currentEntity && this.currentEntity.isValid('publish'), (isValid, previousValue, reactionDisposer) => {
      if (isValid) {
        this.showValidationErrors = false;
        reactionDisposer.dispose();
      }
    });
    return this.currentEntity!.publish();
  }

  @action
  public async distribute(): Promise<void> {
    this.showValidationErrors = true;

    reaction(() => this.currentDistribution && this.currentDistribution.isValid, (isValid, previousValue, reactionDisposer) => {
      if (isValid) {
        this.showValidationErrors = false;
        reactionDisposer.dispose();
      }
    });

    return this.currentDistribution!.distributeAssignment();
  }

  @action
  public resetCurrentArticlesPage() {
    this.currentArticlesPage = 1;
  }

  @action
  public resetArticlesList() {
    this.allArticles = [];
  }

  public getIsDraftSaving(): boolean {
    return this.currentEntity!.getIsDraftSaving();
  }

  public getAllArticles(): Array<Article> {
    return toJS(this.allArticles);
  }

  public getAllGrades(): Array<Grade> {
    return toJS(this.allGrades);
  }

  public getAllSubjects(): Array<Subject> {
    return toJS(this.allSubjects);
  }

  public getAllSources(): Array<Source> {
    return toJS(this.allSources);
  }

  public getAllKeywords(): Array<Keyword> {
    return toJS(this.allKeywords);
  }

  /*public getIsOpen(): boolean {
    return this.open;
  }*/

  @action
  public async getArticles({
    perPage = DEFAULT_AMOUNT_ARTICLES_PER_PAGE,
    isNextPage = false,
    ...rest
  }: Partial<ArticleSearchParams> & { isNextPage?: boolean }) {
    if (!(this.fetchingArticles && isNextPage)) {
      this.fetchingArticles = true;
      this.currentArticlesPage = isNextPage ? this.currentArticlesPage + 1 : this.currentArticlesPage;

      try {
        const articles = await this.articleService.getArticles({
          perPage,
          page: this.currentArticlesPage,
          order: rest.order,
          grades: rest.grades,
          subjects: rest.subjects,
          core: rest.core,
          goal: rest.goal,
          multi: rest.multi,
          source: rest.source,
          searchTitle: rest.searchTitle,
          lang: rest.lang
        });

        runInAction(() => {
          this.allArticles = appendPage(isNextPage ? this.allArticles : [], articles);
          if (articles.length < perPage) {
            this.isFetchedArticlesListFinished = true;
          }
        });
      } catch (e) {
        runInAction(() => { this.allArticles = []; });
        // Reported, not rethrown: none of the nine callers caught it, and the filter panel stayed
        // marked `loadingdata` after a failed fetch (#197).
        reportFailure(e);
      } finally {
        runInAction(() => { this.fetchingArticles = false; });
      }
    }
  }

  @action
  public resetIsFetchedArticlesListFinished() {
    this.isFetchedArticlesListFinished = false;
  }

  public async getGrades() {
    // A failed option list leaves that filter empty and says why; the page carries on (#212).
    try {
      const grades = await this.assignmentService.getGrades();
      runInAction(() => { this.allGrades = grades; });
    } catch (error) {
      reportFailure(error);
    }
  }

  public async getSubjects() {
    try {
      const subjects = await this.assignmentService.getSubjects();
      runInAction(() => { this.allSubjects = subjects; });
    } catch (error) {
      reportFailure(error);
    }
  }

  public async getSources() {
    try {
      const sources = await this.assignmentService.getSources();
      runInAction(() => { this.allSources = sources; });
    } catch (error) {
      reportFailure(error);
    }
  }

  public async getKeywords() {
    const relatedArticles = this.currentEntity!.relatedArticles.map(item => item.id);
    const keywords = await this.draftAssignmentService.getKeywordsFromArticles(relatedArticles);
    runInAction(() => { this.allKeywords = keywords; });
    return this.allKeywords;
  }

  public getGoalsByArticle() {
    const arrayGoals: Array<string> = [];
    if (this.currentEntity!.relatedArticles.length > 0) {
      this.currentEntity!.relatedArticles.forEach((element) => {
        if (element.grepGoals && element.grepGoals.length > 0) {
          element.grepGoals.forEach((e) => {
            if (!arrayGoals.includes(e.kode)) {
              arrayGoals.push(e.kode);
            }
          });
        }
      });
    }
    return String(arrayGoals);
  }

  public async createAssignmentWithArticle(
    postID: number
  ): Promise<{ isFinish: boolean; isCreated: boolean }> {
    const articleResponse = await this.articleService.getArticlesByIds([postID]);
    const searchableArticle = articleResponse[0];

    if (searchableArticle) {
      const assigment: DraftAssignment = await this.createAssigment();
      // Awaited, so the save includes the grades and subjects the article brings (#217).
      await assigment.setArticle(searchableArticle);
      await this.currentEntity!.saveImmediate();
      return { isFinish: true, isCreated: true };
    }

    Notification.create({
      type: NotificationTypes.ERROR,
      title: 'Wrong article id'
    });

    return { isFinish: true, isCreated: false };
  }

  public async fetchQuestionAttachments(typeAttachments: AttachmentContentType): Promise<void> {
    if (this.currentEntity!.relatedArticles.length <= 0 && typeAttachments === AttachmentContentType.image) {
      runInAction(() => { this.questionAttachments = []; });
      return;
    }

    runInAction(() => {
      this.fetchingAttachments = true;
      this.fetchingCustomImageAttachments = true;
    });
    try {
      switch (typeAttachments) {
        case AttachmentContentType.image: {
          const images = await this.articleService.fetchImages(
            this.currentEntity!.relatedArticles.map(getAllChildArticlesIds).flat()
          );
          runInAction(() => { this.questionAttachments = images || []; });
          break;
        }
        case AttachmentContentType.customImage: {
          const response = await this.articleService.fetchCustomImages('', this.currentPage, '');
          runInAction(() => {
            this.questionCustomAttachments =
              (response.myCustomImages).map(item => item).flat() || [];
          });
          break;
        }
        case AttachmentContentType.video: {
          const videos = await this.articleService.fetchVideos(
            this.currentEntity!.relatedArticles.map(getAllChildArticlesIds).flat()
          );
          runInAction(() => { this.questionAttachments = videos || []; });
          break;
        }
        default:
          break;
      }

    } catch (e) {
      runInAction(() => {
        this.questionAttachments = [];
        this.questionCustomAttachments = [];
        this.fetchingAttachments = false;
        this.fetchingCustomImageAttachments = false;
      });
      // Reported here, not thrown: nine call sites, and none did more than log it (#215).
      reportFailure(e);
      return;
    }

    runInAction(() => {
      this.fetchingAttachments = false;
      this.fetchingCustomImageAttachments = false;
    });
  }

  /**
   * The custom-image loader, for the one caller that covers work this store does not do:
   * `AttachmentsList.onRemoveAttachment` awaits `decreaseUse` before it asks for a refetch.
   *
   * Every other caller had this wrong. The two fetch methods below own the flag already —
   * true before the request, false after it — so a component setting it around an
   * *unawaited* call to one of them was switching off a loader the store had just switched
   * on, in the same tick. See docs/mobx-6-class-d.md.
   */
  @action
  public setFetchingCustomImages(value: boolean) {
    this.fetchingCustomImageAttachments = value;
  }

  public async fetchQuestionCustomImagesAttachments(listids: string, typeAttachments: AttachmentContentType, title?: string): Promise<void> {
    runInAction(() => { this.fetchingCustomImageAttachments = true; });
    try {
      switch (typeAttachments) {
        case AttachmentContentType.customImage: {
          const response = title !== '' && title !== undefined && title !== null ? await this.articleService.fetchCustomImages(listids, 0, title) : await this.articleService.fetchCustomImages(listids, this.currentPage, '');
          runInAction(() => {
            this.questionCustomAttachments =
              (response.myCustomImages).map(item => item).flat() || [];
            this.numberOfPages = response.total_pages;
          });
          break;
        }
        default:
          break;
      }
    } catch (e) {
      runInAction(() => {
        this.questionCustomAttachments = [];
        this.fetchingCustomImageAttachments = false;
      });
      reportFailure(e);
      return;
    }
    runInAction(() => { this.fetchingCustomImageAttachments = false; });
  }

  public async fetchQuestionCustomImagesAttachmentsAfterUploading(listids: string, typeAttachments: AttachmentContentType, title?: string): Promise<void> {
    runInAction(() => { this.fetchingCustomImageAttachments = true; });
    try {
      switch (typeAttachments) {
        case AttachmentContentType.customImage: {
          const response = title !== '' && title !== undefined && title !== null ? await this.articleService.fetchCustomImages(listids, 0, title) : await this.articleService.fetchCustomImages(listids, 0, '');
          runInAction(() => {
            this.questionCustomAttachments =
              (response.myCustomImages).map(item => item).flat() || [];
            this.numberOfPages = response.total_pages;
          });
          break;
        }
        default:
          break;
      }
    } catch (e) {
      runInAction(() => {
        this.questionCustomAttachments = [];
        this.fetchingCustomImageAttachments = false;
      });
      reportFailure(e);
      return;
    }
    runInAction(() => { this.fetchingCustomImageAttachments = false; });
  }

  public getUpdatedAt() {
    return this.currentEntity!.updatedAt;
  }

  public duplicateQuestion(question: EditableQuestion) {
    this.currentEntity!.addQuestion(
      question.duplicateWithOrder(this.currentEntity!.questions.length)
    );
  }

  @action
  public clearAssignmentContainer() {
    this.assignmentContainer = null;
    this.showValidationErrors = false;
  }

  public getCurrentLocale() {
    return this.userService.getCurrentLocale() || Locales.EN;
  }

  public getCurrentUser = () => (
    this.userService.getCurrentUser()
  )

  public getPostID() {
    return this.userService.getPostID();
  }

  public setPostID(postID: number) {
    this.userService.setPostID(postID);
  }

  public getReferralToken() {
    return this.userService.getAssignmentReferralToken();
  }

  public setReferralToken(referralToken: string) {
    this.userService.setAssignmentReferralToken(referralToken);
  }

  public getAssignmentIdFromLocalStorage() {
    return this.userService.getAssignmentId();
  }

  public setAssignmentIdToLocalStorage(assignmentId: string) {
    this.userService.setAssignmentId(assignmentId);
  }

  public getAllArticlePanelFilters() {
    return toJS(this.allArticlePanelFilters);
  }

  public async getFiltersArticlePanel(lang: string) {
    const filters = await this.teachingPathService.getFiltersArticlePanel(lang);
    runInAction(() => { this.allArticlePanelFilters = filters; });
  }

  public async assignStudentToAssignment(assignmentId: string, referralToken: string) {
    return this.distributionService.assignStudentToAssignment(assignmentId, referralToken);
  }

  public async getGradeWpIds(gradeWpIds: Array<number>) {
    return this.teachingPathService.getGradeWpIds(gradeWpIds);
  }

  public async getSubjectWpIds(subjectWpIds: Array<number>) {
    return this.teachingPathService.getSubjectWpIds(subjectWpIds);
  }

  public async getGrepFilters(grades: string, subjects: string) {
    return this.teachingPathService.getGrepFilters(grades, subjects);
  }
  public async getGrepFiltersAssignment(locale: string, grades: string, subjects: string, coreElements?: string, goals?: string) {
    return this.assignmentService.getGrepFiltersAssignment(locale, grades, subjects, coreElements, goals);
  }
  public async getGrepFiltersMyAssignment(locale: string, grades: string, subjects: string, coreElements?: string, goals?: string) {
    return this.assignmentService.getGrepFiltersMyAssignment(locale, grades, subjects, coreElements, goals);
  }
  public async getGrepFiltersMySchoolAssignment(locale: string, grades: string, subjects: string, coreElements?: string, goals?: string) {
    return this.assignmentService.getGrepFiltersMySchoolAssignment(locale, grades, subjects, coreElements, goals);
  }
  public async getGrepGoalsFilters(grepCoreElementsIds: Array<number>, grepMainTopicsIds: Array<number>, gradesIds: Array<number>, subjectsIds: Array<number>, orderGoalsCodes: Array<string>, perPage: number, page: number) {
    return this.teachingPathService.getGrepGoalsFilters(grepCoreElementsIds, grepMainTopicsIds, gradesIds, subjectsIds, orderGoalsCodes, perPage, page);
  }
  @action
  public async downloadTeacherGuidancePDF(id: number) {
    return this.assignmentService.downloadTeacherGuidancePDF(id);
  }
  @action
  public openTeacherGuidanceAssig = (nroLevel: string): void => {

    const modalTG = Array.from(document.getElementsByClassName('modalContentTGAssig') as HTMLCollectionOf<HTMLElement>);
    const scrollsideElement = Array.from(document.getElementsByClassName('addAssignmentFlow w50') as HTMLCollectionOf<HTMLElement>);
    const blurBackground = Array.from(document.getElementsByClassName('blur') as HTMLCollectionOf<HTMLElement>);
    const bgpreview = Array.from(document.getElementsByClassName('hide') as HTMLCollectionOf<HTMLElement>);
    // for assigment preview
    if (bgpreview !== null && bgpreview !== undefined && bgpreview.length > 0) {
      bgpreview[0].classList.remove('hide');
    }

    if (scrollsideElement !== null && scrollsideElement !== undefined && scrollsideElement.length > 0) {
      scrollsideElement[0].classList.add('notShow');
    }

    if (blurBackground !== null && blurBackground !== undefined && blurBackground.length > 0) {
      blurBackground[0].classList.remove('blur');
      blurBackground[0].classList.add('blurShowed');
    }

    modalTG[0].classList.add('open');
    /* modalTGBack[0].classList.add('hide'); */

    setTimeout(
      () => {
        const editDescript = (document.getElementsByClassName(`jr-desEdit${nroLevel}`)[0] as HTMLDivElement);
        const editInputText = (editDescript.getElementsByClassName('ql-editor')[0] as HTMLInputElement);
        editInputText.focus();
      },
      delayFocus
    );
  }

  @action
  public closeModalTG = () => {
    const modalTG = Array.from(document.getElementsByClassName('modalContentTGAssig') as HTMLCollectionOf<HTMLElement>);
    const modalTGBack = Array.from(document.getElementsByClassName('modalContentTGAssigBackground') as HTMLCollectionOf<HTMLElement>);
    const bgpreview = Array.from(document.getElementsByClassName('modalContentTGAssigBackground') as HTMLCollectionOf<HTMLElement>);
    const scrollsideElement = Array.from(document.getElementsByClassName('addAssignmentFlow w50') as HTMLCollectionOf<HTMLElement>);
    const blurBackground = Array.from(document.getElementsByClassName('blurShowed') as HTMLCollectionOf<HTMLElement>);

    // for assigment preview
    if (bgpreview !== null && bgpreview !== undefined && bgpreview.length > 0) {
      bgpreview[0].classList.add('hide');
    }

    // for assignment creaton
    if (scrollsideElement !== null && scrollsideElement !== undefined && scrollsideElement.length > 0) {
      scrollsideElement[0].classList.remove('notShow');
    }
    if (blurBackground !== null && blurBackground !== undefined && blurBackground.length > 0) {
      blurBackground[0].classList.add('blur');
      blurBackground[0].classList.remove('blurShowed');
    }
    modalTG[0].classList.remove('open');
    modalTGBack[0].classList.add('hide');

  }
}
